import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Spinner, Badge, Row, Col, InputGroup, Card, Tabs, Tab } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import api from '../../api/axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkStockValue, setBulkStockValue] = useState('');
  const [activeViewTab, setActiveViewTab] = useState('list');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
  const [selectedMediaPreviews, setSelectedMediaPreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [linkedProductIds, setLinkedProductIds] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [mediaOrder, setMediaOrder] = useState([]);
  const [draggedMediaId, setDraggedMediaId] = useState(null);
  const [draggedLinkedId, setDraggedLinkedId] = useState(null);
  const [draggedProductId, setDraggedProductId] = useState(null);
  const [dragOverSubcategoryKey, setDragOverSubcategoryKey] = useState('');
  const [draggedSubcategoryId, setDraggedSubcategoryId] = useState(null);
  const [dragOverMainCategoryId, setDragOverMainCategoryId] = useState('');
  const [draggedMainCategoryId, setDraggedMainCategoryId] = useState(null);
  const [collapsedMainCategoryIds, setCollapsedMainCategoryIds] = useState({});
  const [collapsedSubcategoryIds, setCollapsedSubcategoryIds] = useState({});
  const [editingCatalogCategory, setEditingCatalogCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    category_type: 'main',
    name: '',
    slug: '',
    description: '',
    manual_url: '',
    image_url: '',
    parent_id: '',
    display_order: 0,
    is_visible: true
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    manual_url: '',
    manufacturer: '',
    model: '',
    product_id: '',
    product_code: '',
    price: '',
    stock: '',
    category_id: '',
    is_visible: true
  });
  const { t } = useLanguage();
  const { notify, confirm } = useNotification();
  const location = useLocation();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const requestedTab = (params.get('tab') || '').toLowerCase();
    if (requestedTab === 'catalog' || requestedTab === 'tree') {
      setActiveViewTab('tree');
      return;
    }
    if (requestedTab === 'list' || requestedTab === 'products') {
      setActiveViewTab('list');
    }
  }, [location.search]);

  useEffect(() => {
    setSelectedProductIds((prev) => prev.filter((id) => products.some((product) => product.id === id)));
  }, [products]);

  const categoryNameByKey = useMemo(() => {
    return new Map(categories.map((category) => [String(category.id), category.name]));
  }, [categories]);

  const categoryLabelByEntityId = useMemo(() => {
    return new Map(
      categories
        .filter((category) => category.category_type === 'sub')
        .map((category) => {
          const parentName = categoryNameByKey.get(String(category.parent_id)) || '';
          const label = parentName ? `${parentName} -> ${category.name}` : category.name;
          return [String(category.entity_id || category.id), label];
        })
    );
  }, [categories, categoryNameByKey]);

  const subcategoryOptions = useMemo(() => {
    return categories
      .filter((category) => category.category_type === 'sub')
      .map((category) => ({
        ...category,
        optionValue: String(category.entity_id || category.id),
        parentName: categoryNameByKey.get(String(category.parent_id)) || ''
      }));
  }, [categories, categoryNameByKey]);

  const filteredProducts = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) {
      return products;
    }

    return products.filter((product) => {
      const haystack = [
        product.name,
        product.description,
        product.manufacturer,
        product.model,
        product.product_id,
        product.product_code,
        categoryLabelByEntityId.get(String(product.category_id || '')),
        product.is_visible ? 'visible' : 'hidden'
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(value);
    });
  }, [products, searchTerm, categoryLabelByEntityId]);

  const allFilteredSelected = filteredProducts.length > 0 && filteredProducts.every((product) => selectedProductIds.includes(product.id));

  const isMainCategoryCollapsed = (mainCategoryId) => Boolean(collapsedMainCategoryIds[String(mainCategoryId)]);
  const isSubcategoryCollapsed = (subcategoryId) => Boolean(collapsedSubcategoryIds[String(subcategoryId)]);

  const toggleMainCategoryCollapsed = (mainCategoryId) => {
    const key = String(mainCategoryId);
    setCollapsedMainCategoryIds((previous) => ({
      ...previous,
      [key]: !previous[key]
    }));
  };

  const toggleSubcategoryCollapsed = (subcategoryId) => {
    const key = String(subcategoryId);
    setCollapsedSubcategoryIds((previous) => ({
      ...previous,
      [key]: !previous[key]
    }));
  };

  const slugifyCategory = (value) => String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const handleOpenCategoryModal = ({ category = null, categoryType = 'main', parentId = '' } = {}) => {
    if (category) {
      setEditingCatalogCategory(category);
      setCategoryFormData({
        category_type: category.category_type || 'main',
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        manual_url: category.manual_url || '',
        image_url: category.image_url || '',
        parent_id: category.parent_id || '',
        display_order: category.display_order || 0,
        is_visible: category.is_visible !== false
      });
    } else {
      setEditingCatalogCategory(null);
      setCategoryFormData({
        category_type: categoryType,
        name: '',
        slug: '',
        description: '',
        manual_url: '',
        image_url: '',
        parent_id: categoryType === 'sub' ? String(parentId || '') : '',
        display_order: 0,
        is_visible: true
      });
    }

    setShowCategoryModal(true);
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCatalogCategory(null);
  };

  const handleCategoryFormChange = (event) => {
    const { name, type, value, checked } = event.target;
    const normalizedValue = type === 'checkbox' ? checked : value;

    setCategoryFormData((previous) => {
      const next = {
        ...previous,
        [name]: normalizedValue
      };

      if (name === 'name' && !editingCatalogCategory) {
        next.slug = slugifyCategory(normalizedValue);
      }

      if (name === 'category_type' && normalizedValue === 'main') {
        next.parent_id = '';
      }

      return next;
    });
  };

  const handleSubmitCategoryForm = async (event) => {
    event.preventDefault();

    const payload = {
      ...categoryFormData,
      parent_id: categoryFormData.category_type === 'sub' ? categoryFormData.parent_id : '',
      display_order: Number(categoryFormData.display_order || 0),
      is_visible: categoryFormData.is_visible !== false
    };

    if (!payload.name || !payload.slug) {
      notify({ variant: 'warning', message: 'Name and slug are required' });
      return;
    }

    if (payload.category_type === 'sub' && !payload.parent_id) {
      notify({ variant: 'warning', message: 'Main category is required for subcategory' });
      return;
    }

    try {
      if (editingCatalogCategory) {
        await api.put(`/api/admin/categories/${editingCatalogCategory.id}`, payload);
      } else {
        await api.post('/api/admin/categories', payload);
      }

      notify({
        variant: 'success',
        message: editingCatalogCategory ? 'Category updated successfully' : 'Category created successfully'
      });

      handleCloseCategoryModal();
      await fetchCategories();
    } catch (err) {
      notify({ variant: 'danger', message: err.response?.data?.error || t('common.error') });
    }
  };

  const productTreeByMainCategory = useMemo(() => {
    const mainCategories = categories.filter((category) => category.category_type === 'main');
    const subcategories = categories.filter((category) => category.category_type === 'sub');

    const productsBySubcategoryId = filteredProducts.reduce((acc, product) => {
      const key = String(product.category_id || '');
      if (!key) {
        return acc;
      }

      if (!acc.has(key)) {
        acc.set(key, []);
      }
      acc.get(key).push(product);
      return acc;
    }, new Map());

    const subcategoriesByMainId = subcategories.reduce((acc, subcategory) => {
      const key = String(subcategory.parent_id || '');
      if (!acc.has(key)) {
        acc.set(key, []);
      }
      acc.get(key).push({
        ...subcategory,
        products: productsBySubcategoryId.get(String(subcategory.entity_id || subcategory.id)) || []
      });
      return acc;
    }, new Map());

    return mainCategories
      .map((mainCategory) => ({
        ...mainCategory,
        subcategories: (subcategoriesByMainId.get(String(mainCategory.id)) || []).sort((left, right) => {
          const leftOrder = Number(left.display_order || 0);
          const rightOrder = Number(right.display_order || 0);
          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }
          return String(left.name || '').localeCompare(String(right.name || ''));
        })
      }))
      .filter((mainCategory) => mainCategory.subcategories.length > 0);
  }, [categories, filteredProducts]);

  const handleExpandAllCatalog = () => {
    setCollapsedMainCategoryIds({});
    setCollapsedSubcategoryIds({});
  };

  const handleCollapseAllCatalog = () => {
    const nextMainState = {};
    const nextSubState = {};

    for (const mainCategory of productTreeByMainCategory) {
      nextMainState[String(mainCategory.id)] = true;
      for (const subcategory of mainCategory.subcategories || []) {
        nextSubState[String(subcategory.id)] = true;
      }
    }

    setCollapsedMainCategoryIds(nextMainState);
    setCollapsedSubcategoryIds(nextSubState);
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/admin/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/products');
      const nextProducts = response.data;
      setProducts(nextProducts);
      if (editingProduct) {
        const updated = nextProducts.find((product) => product.id === editingProduct.id);
        if (updated) {
          setEditingProduct(updated);
          setMediaOrder(updated.images || []);
        }
      }
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setMediaOrder(product.images || []);
      setFormData({
        name: product.name,
        description: product.description,
        manual_url: product.manual_url || '',
        manufacturer: product.manufacturer || '',
        model: product.model || '',
        product_id: product.product_id || '',
        product_code: product.product_code || '',
        price: product.price,
        stock: product.stock,
        category_id: product.category_id || '',
        is_visible: product.is_visible
      });
      fetchLinkedProducts(product.id);
    } else {
      setEditingProduct(null);
      setMediaOrder([]);
      setFormData({
        name: '',
        description: '',
        manual_url: '',
        manufacturer: '',
        model: '',
        product_id: '',
        product_code: '',
        price: '',
        stock: '',
        category_id: '',
        is_visible: true
      });
      setLinkedProductIds([]);
    }
    setSelectedMediaFiles([]);
    setSelectedMediaPreviews([]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setSelectedMediaFiles([]);
    setSelectedMediaPreviews([]);
    setIsDragging(false);
    setLinkedProductIds([]);
    setMediaOrder([]);
    setDraggedMediaId(null);
    setDraggedLinkedId(null);
    setDraggedProductId(null);
    setDragOverSubcategoryKey('');
    setDraggedSubcategoryId(null);
    setDragOverMainCategoryId('');
    setDraggedMainCategoryId(null);
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category_id) {
      notify({ variant: 'warning', message: 'Subcategory is required' });
      return;
    }
    
    try {
      if (editingProduct) {
        await api.put(`/api/admin/products/${editingProduct.id}`, formData);
        await updateLinkedProducts(editingProduct.id);
      } else {
        await api.post('/api/admin/products', formData);
      }
      
      notify({ variant: 'success', message: t('common.success') });
      handleCloseModal();
      fetchProducts();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to save product:', err);
    }
  };

  const fetchLinkedProducts = async (productId) => {
    try {
      setLinkedLoading(true);
      const response = await api.get(`/api/admin/products/${productId}/linked`);
      const orderedIds = (response.data || [])
        .sort((a, b) => a.display_order - b.display_order)
        .map((link) => link.linked_product_id);
      setLinkedProductIds(orderedIds);
    } catch (err) {
      console.error('Failed to fetch linked products:', err);
      setLinkedProductIds([]);
    } finally {
      setLinkedLoading(false);
    }
  };

  const updateLinkedProducts = async (productId, nextLinkedIds = linkedProductIds) => {
    await api.put(`/api/admin/products/${productId}/linked`, {
      linkedProductIds: nextLinkedIds
    });
  };

  const toggleLinkedProduct = (productId) => {
    setLinkedProductIds((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const handleLinkedDragStart = (productId) => {
    setDraggedLinkedId(productId);
  };

  const handleLinkedDrop = async (targetId) => {
    if (!draggedLinkedId || draggedLinkedId === targetId) {
      return;
    }

    const nextOrder = [...linkedProductIds];
    const fromIndex = nextOrder.indexOf(draggedLinkedId);
    const toIndex = nextOrder.indexOf(targetId);

    if (fromIndex === -1 || toIndex === -1) {
      return;
    }

    nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, draggedLinkedId);
    setLinkedProductIds(nextOrder);
    setDraggedLinkedId(null);

    if (editingProduct) {
      await updateLinkedProducts(editingProduct.id, nextOrder);
    }
  };

  const handleDelete = async (productId) => {
    const confirmed = await confirm({
      title: 'Delete product',
      message: 'Are you sure you want to delete this product?',
      confirmText: 'Delete'
    });

    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/api/admin/products/${productId}`);
      notify({ variant: 'success', message: t('common.success') });
      fetchProducts();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to delete product:', err);
    }
  };

  const handleMoveProductToSubcategory = async (productId, targetSubcategoryId) => {
    const product = products.find((item) => item.id === productId);
    if (!product) {
      return;
    }

    if (String(product.category_id || '') === String(targetSubcategoryId)) {
      return;
    }

    try {
      await api.put(`/api/admin/products/${product.id}`, {
        name: product.name,
        description: product.description || '',
        manual_url: product.manual_url || '',
        manufacturer: product.manufacturer || '',
        model: product.model || '',
        product_id: product.product_id || '',
        product_code: product.product_code || '',
        price: product.price,
        stock: product.stock,
        category_id: targetSubcategoryId,
        is_visible: product.is_visible
      });

      notify({ variant: 'success', message: `Moved ${product.name}` });
      await fetchProducts();
    } catch (err) {
      notify({ variant: 'danger', message: err.response?.data?.error || t('common.error') });
    }
  };

  const handleMoveSubcategoryToMainCategory = async (subcategoryId, targetMainCategoryId) => {
    const subcategory = categories.find((category) => String(category.id) === String(subcategoryId));
    if (!subcategory || subcategory.category_type !== 'sub') {
      return;
    }

    try {
      const sourceParentId = String(subcategory.parent_id || '');
      const targetParentId = String(targetMainCategoryId || '');

      const getSortedSubcategories = (parentId, excludingId = null) => (
        categories
          .filter((category) => category.category_type === 'sub' && String(category.parent_id || '') === String(parentId || '') && String(category.id) !== String(excludingId || ''))
          .sort((left, right) => {
            const leftOrder = Number(left.display_order || 0);
            const rightOrder = Number(right.display_order || 0);
            if (leftOrder !== rightOrder) {
              return leftOrder - rightOrder;
            }
            return String(left.name || '').localeCompare(String(right.name || ''));
          })
      );

      const updateCategory = async (category, overrides = {}) => {
        await api.put(`/api/admin/categories/${category.id}`, {
          category_type: category.category_type,
          name: category.name,
          slug: category.slug,
          description: category.description || '',
          manual_url: category.manual_url || '',
          image_url: category.image_url || '',
          parent_id: category.parent_id || '',
          display_order: category.display_order || 0,
          is_visible: category.is_visible !== false,
          ...overrides
        });
      };

      const targetSiblings = getSortedSubcategories(targetParentId, subcategory.id);
      const reorderedTargetSiblings = [...targetSiblings, subcategory];

      for (let index = 0; index < reorderedTargetSiblings.length; index += 1) {
        const sibling = reorderedTargetSiblings[index];
        await updateCategory(sibling, {
          parent_id: targetParentId,
          display_order: index
        });
      }

      if (sourceParentId !== targetParentId) {
        const sourceSiblings = getSortedSubcategories(sourceParentId, subcategory.id);
        for (let index = 0; index < sourceSiblings.length; index += 1) {
          const sibling = sourceSiblings[index];
          await updateCategory(sibling, {
            parent_id: sourceParentId,
            display_order: index
          });
        }
      }

      notify({ variant: 'success', message: `Moved subcategory ${subcategory.name}` });
      await fetchCategories();
    } catch (err) {
      notify({ variant: 'danger', message: err.response?.data?.error || t('common.error') });
    }
  };

  const handleReorderSubcategory = async (movedSubcategoryId, targetSubcategoryId) => {
    if (String(movedSubcategoryId) === String(targetSubcategoryId)) {
      return;
    }

    const movedSubcategory = categories.find((category) => String(category.id) === String(movedSubcategoryId));
    const targetSubcategory = categories.find((category) => String(category.id) === String(targetSubcategoryId));
    if (!movedSubcategory || !targetSubcategory || movedSubcategory.category_type !== 'sub' || targetSubcategory.category_type !== 'sub') {
      return;
    }

    const sourceParentId = String(movedSubcategory.parent_id || '');
    const targetParentId = String(targetSubcategory.parent_id || '');

    const sortSubcategories = (parentId, excludingId = null) => (
      categories
        .filter((category) => category.category_type === 'sub' && String(category.parent_id || '') === String(parentId || '') && String(category.id) !== String(excludingId || ''))
        .sort((left, right) => {
          const leftOrder = Number(left.display_order || 0);
          const rightOrder = Number(right.display_order || 0);
          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }
          return String(left.name || '').localeCompare(String(right.name || ''));
        })
    );

    const updateCategory = async (category, overrides = {}) => {
      await api.put(`/api/admin/categories/${category.id}`, {
        category_type: category.category_type,
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        manual_url: category.manual_url || '',
        image_url: category.image_url || '',
        parent_id: category.parent_id || '',
        display_order: category.display_order || 0,
        is_visible: category.is_visible !== false,
        ...overrides
      });
    };

    try {
      const targetSiblings = sortSubcategories(targetParentId, movedSubcategory.id);
      const targetIndex = targetSiblings.findIndex((category) => String(category.id) === String(targetSubcategory.id));
      const reorderedTargetSiblings = [...targetSiblings];
      reorderedTargetSiblings.splice(targetIndex >= 0 ? targetIndex : reorderedTargetSiblings.length, 0, movedSubcategory);

      for (let index = 0; index < reorderedTargetSiblings.length; index += 1) {
        const sibling = reorderedTargetSiblings[index];
        await updateCategory(sibling, {
          parent_id: targetParentId,
          display_order: index
        });
      }

      if (sourceParentId !== targetParentId) {
        const sourceSiblings = sortSubcategories(sourceParentId, movedSubcategory.id);
        for (let index = 0; index < sourceSiblings.length; index += 1) {
          const sibling = sourceSiblings[index];
          await updateCategory(sibling, {
            parent_id: sourceParentId,
            display_order: index
          });
        }
      }

      notify({ variant: 'success', message: `Reordered subcategory ${movedSubcategory.name}` });
      await fetchCategories();
    } catch (err) {
      notify({ variant: 'danger', message: err.response?.data?.error || t('common.error') });
    }
  };

  const handleReorderMainCategory = async (movedMainCategoryId, targetMainCategoryId) => {
    if (String(movedMainCategoryId) === String(targetMainCategoryId)) {
      return;
    }

    const mainCategories = categories
      .filter((category) => category.category_type === 'main')
      .sort((left, right) => {
        const leftOrder = Number(left.display_order || 0);
        const rightOrder = Number(right.display_order || 0);
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return String(left.name || '').localeCompare(String(right.name || ''));
      });

    const movedIndex = mainCategories.findIndex((category) => String(category.id) === String(movedMainCategoryId));
    const targetIndex = mainCategories.findIndex((category) => String(category.id) === String(targetMainCategoryId));
    if (movedIndex < 0 || targetIndex < 0) {
      return;
    }

    const reordered = [...mainCategories];
    const [moved] = reordered.splice(movedIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    try {
      for (let index = 0; index < reordered.length; index += 1) {
        const category = reordered[index];
        await api.put(`/api/admin/categories/${category.id}`, {
          category_type: category.category_type,
          name: category.name,
          slug: category.slug,
          description: category.description || '',
          manual_url: category.manual_url || '',
          image_url: category.image_url || '',
          parent_id: category.parent_id || '',
          display_order: index,
          is_visible: category.is_visible !== false
        });
      }

      notify({ variant: 'success', message: 'Main category order updated' });
      await fetchCategories();
    } catch (err) {
      notify({ variant: 'danger', message: err.response?.data?.error || t('common.error') });
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds((prev) => (
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    ));
  };

  const toggleSelectAllFilteredProducts = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredProducts.map((product) => product.id));
      setSelectedProductIds((prev) => prev.filter((id) => !filteredIds.has(id)));
      return;
    }

    setSelectedProductIds((prev) => Array.from(new Set([...prev, ...filteredProducts.map((product) => product.id)])));
  };

  const handleBulkProducts = async () => {
    if (selectedProductIds.length === 0) {
      notify({ variant: 'warning', message: 'Select at least one product first' });
      return;
    }

    if (!bulkAction) {
      notify({ variant: 'warning', message: 'Select a bulk action first' });
      return;
    }

    if (bulkAction === 'set_stock' && (bulkStockValue === '' || Number.isNaN(Number(bulkStockValue)) || Number(bulkStockValue) < 0)) {
      notify({ variant: 'warning', message: 'Enter a valid stock value' });
      return;
    }

    const actionLabel = {
      show: 'show selected products to customers',
      hide: 'hide selected products from customers',
      set_stock: `set stock to ${bulkStockValue} for selected products`,
      delete: 'delete selected products'
    }[bulkAction];

    const confirmed = await confirm({
      title: 'Bulk product action',
      message: `Are you sure you want to ${actionLabel}?`,
      confirmText: 'Apply'
    });

    if (!confirmed) {
      return;
    }

    try {
      setBulkLoading(true);
      const payload = { ids: selectedProductIds, action: bulkAction };
      if (bulkAction === 'set_stock') {
        payload.stock = Number(bulkStockValue);
      }

      const response = await api.post('/api/admin/products/bulk', payload);
      const result = response.data;
      const errorCount = result.errors?.length || 0;
      notify({
        variant: errorCount > 0 ? 'warning' : 'success',
        message: `Processed ${result.processed} products${errorCount > 0 ? `, ${errorCount} errors` : ''}`
      });
      if (errorCount > 0) {
        setError(result.errors.map((item) => `#${item.id}: ${item.error}`).join(' | '));
      } else {
        setError(null);
      }
      setSelectedProductIds([]);
      setBulkAction('');
      setBulkStockValue('');
      fetchProducts();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to run bulk product action'
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleImageUpload = async (productId, files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    try {
      await api.post(`/api/admin/products/${productId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      notify({ variant: 'success', message: 'Media uploaded successfully' });
      fetchProducts();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to upload media'
      });
      console.error('Failed to upload media:', err);
    }
  };

  const handleMediaSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      setSelectedMediaFiles([]);
      setSelectedMediaPreviews([]);
      return;
    }

    setSelectedMediaFiles(files);
    setSelectedMediaPreviews(files.map((file) => ({
      name: file.name,
      type: file.type,
      url: URL.createObjectURL(file)
    })));
  };

  const handleUploadSelectedMedia = async () => {
    if (!editingProduct || selectedMediaFiles.length === 0) {
      return;
    }

    await handleImageUpload(editingProduct.id, selectedMediaFiles);
    setSelectedMediaFiles([]);
    setSelectedMediaPreviews([]);
  };

  const updateMediaOrder = async (productId, nextOrder) => {
    await api.put(`/api/admin/products/${productId}/images/order`, {
      orderedImageIds: nextOrder.map((media) => media.id)
    });
  };

  const handleMediaDragStart = (imageId) => {
    setDraggedMediaId(imageId);
  };

  const handleMediaDrop = async (targetId) => {
    if (!draggedMediaId || draggedMediaId === targetId || !editingProduct) {
      return;
    }

    const nextOrder = [...mediaOrder];
    const fromIndex = nextOrder.findIndex((media) => media.id === draggedMediaId);
    const toIndex = nextOrder.findIndex((media) => media.id === targetId);

    if (fromIndex === -1 || toIndex === -1) {
      return;
    }

    const [moved] = nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, moved);
    setMediaOrder(nextOrder);
    setDraggedMediaId(null);
    await updateMediaOrder(editingProduct.id, nextOrder);
  };

  const setCoverImage = async (imageId) => {
    if (!editingProduct) {
      return;
    }

    const nextOrder = [...mediaOrder];
    const coverIndex = nextOrder.findIndex((media) => media.id === imageId);
    if (coverIndex <= 0) {
      return;
    }

    const [cover] = nextOrder.splice(coverIndex, 1);
    nextOrder.unshift(cover);
    setMediaOrder(nextOrder);
    await updateMediaOrder(editingProduct.id, nextOrder);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length === 0) {
      return;
    }

    setSelectedMediaFiles(files);
    setSelectedMediaPreviews(files.map((file) => ({
      name: file.name,
      type: file.type,
      url: URL.createObjectURL(file)
    })));
  };

  const handleDeleteMedia = async (productId, imageId) => {
    const confirmed = await confirm({
      title: 'Delete media',
      message: 'Delete this media file?',
      confirmText: 'Delete'
    });

    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/api/admin/products/${productId}/images/${imageId}`);
      notify({ variant: 'success', message: t('common.success') });
      fetchProducts();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to delete media:', err);
    }
  };

  const handleExportProducts = async () => {
    try {
      const response = await api.get('/api/admin/products/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products-export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      notify({ variant: 'success', message: 'Products exported successfully' });
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to export products'
      });
      console.error('Failed to export products:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/admin/products/import-template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product-import-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      notify({ variant: 'success', message: 'Template downloaded successfully' });
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to download template'
      });
      console.error('Failed to download template:', err);
    }
  };

  const handleImportProducts = async () => {
    if (!importFile) {
      notify({ variant: 'warning', message: 'Please select a file to import' });
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await api.post('/api/admin/products/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      notify({ variant: 'success', message: 'Import completed' });
      fetchProducts();
      setImportFile(null);
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to import products'
      });
      console.error('Failed to import products:', err);
    } finally {
      setImporting(false);
    }
  };

  const handleSyncStockValues = async () => {
    try {
      setSyncLoading(true);
      const response = await api.post('/api/admin/products/sync-stock');
      const syncResult = response.data?.result || {};
      notify({
        variant: 'success',
        message: `Stock sync completed. Updated ${syncResult.updatedProducts || 0} product(s).`
      });
      await fetchProducts();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to sync stock values'
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const formatImportError = (item) => {
    if (!item) return 'Unknown import error';
    if (typeof item === 'string') return item;

    const rowText = item.row ? `Row ${item.row}` : 'Row ?';
    const productText = item.product ? ` (${item.product})` : '';
    const messageText = item.error || 'Unknown import error';

    return `${rowText}${productText}: ${messageText}`;
  };

  const getMediaUrl = (media) => {
    if (!media) return '';
    const rawUrl = typeof media === 'string'
      ? media
      : (media.imageUrl || media.image_url || media.url || media.path || '');
    if (!rawUrl) return '';
    if (rawUrl.startsWith('/uploads') && api?.defaults?.baseURL) {
      return `${api.defaults.baseURL}${rawUrl}`;
    }
    if (rawUrl.startsWith('uploads/') && api?.defaults?.baseURL) {
      return `${api.defaults.baseURL}/${rawUrl}`;
    }
    if (rawUrl.startsWith('http://') && window.location.protocol === 'https:') {
      return rawUrl.replace('http://', 'https://');
    }
    return rawUrl;
  };

  const isVideo = (media) => {
    const url = getMediaUrl(media);
    return /\.(mp4|webm|ogg)$/i.test(url);
  };

  const formatSyncedAt = (value) => {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleString();
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('admin.products')}</h1>
        <div className="d-flex gap-2">
          <Button
            variant="outline-dark"
            onClick={handleSyncStockValues}
            disabled={syncLoading}
            title="Sync stock values"
            aria-label="Sync stock values"
          >
            {syncLoading ? 'Syncing...' : 'Sync values'}
          </Button>
          <Button variant="outline-success" onClick={handleExportProducts} title={t('admin.exportExcel')} aria-label={t('admin.exportExcel')} style={{fontSize:'1.75rem'}}>
            📊
          </Button>
          <Button variant="outline-primary" onClick={() => setShowImportModal(true)} title={t('admin.importExcel')} aria-label={t('admin.importExcel')} style={{fontSize:'1.75rem'}}>
            📥
          </Button>
          <Button variant="primary" onClick={() => handleShowModal()} title={t('admin.addProduct')} aria-label={t('admin.addProduct')} style={{fontSize:'1.75rem'}}>
            ⊕
          </Button>
        </div>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Label>{t('admin.filterProducts')}</Form.Label>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={`${t('admin.filterProducts')}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label>{t('admin.bulkAction')}</Form.Label>
              <Form.Select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} disabled={bulkLoading}>
                <option value="">{t('admin.selectAction')}</option>
                <option value="show">Show to customer</option>
                <option value="hide">Hide from customer</option>
                <option value="set_stock">Set stock value</option>
                <option value="delete">{t('common.delete')}</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>{t('admin.stock')}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                placeholder="Value"
                value={bulkStockValue}
                onChange={(e) => setBulkStockValue(e.target.value)}
                disabled={bulkAction !== 'set_stock' || bulkLoading}
              />
            </Col>
            <Col md={2}>
              <div className="small text-muted mb-2">{t('admin.selected')} {selectedProductIds.length}</div>
              <Button className="w-100" variant="primary" onClick={handleBulkProducts} disabled={bulkLoading || selectedProductIds.length === 0}>
                {bulkLoading ? t('admin.applying') : t('admin.apply')}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Tabs activeKey={activeViewTab} onSelect={(key) => setActiveViewTab(key || 'list')} className="mb-3">
            <Tab eventKey="list" title={t('admin.productListTab')}>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th style={{ width: '44px' }}>
                      <Form.Check type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAllFilteredProducts} aria-label="Select all filtered products" />
                    </th>
                    <th>{t('admin.id')}</th>
                    <th>{t('admin.name')}</th>
                    <th>{t('admin.manufacturer')}</th>
                    <th>{t('admin.category')}</th>
                    <th>{t('admin.stock')}</th>
                    <th>Last synced</th>
                    <th>{t('admin.visible')}</th>
                    <th>{t('admin.images')}</th>
                    <th>{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedProductIds.includes(product.id)}
                          onChange={() => toggleProductSelection(product.id)}
                          aria-label={`Select product ${product.name}`}
                        />
                      </td>
                      <td>{product.id}</td>
                      <td>{product.name}</td>
                      <td>{product.manufacturer || '-'}</td>
                      <td>{categoryLabelByEntityId.get(String(product.category_id || '')) || '-'}</td>
                      <td>{product.stock}</td>
                      <td>{formatSyncedAt(product.last_synced_at)}</td>
                      <td>
                        {product.is_visible ? (
                          <Badge bg="success">{t('admin.visible')}</Badge>
                        ) : (
                          <Badge bg="secondary">{t('admin.hidden')}</Badge>
                        )}
                      </td>
                      <td>
                        {product.images?.length || 0} media
                      </td>
                      <td>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={() => handleShowModal(product)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(product.id)}
                        >
                          {t('common.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {filteredProducts.length === 0 && (
                <Alert variant="info" className="mb-0">{t('products.noResults')}</Alert>
              )}
            </Tab>

            <Tab eventKey="tree" title={t('admin.catalogTab')}>
              <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{ fontSize: '1.75rem', lineHeight: 1 }}
                    onClick={() => handleOpenCategoryModal({ categoryType: 'main' })}
                    title="Add main category"
                    aria-label="Add main category"
                  >
                    ⊕
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    style={{ fontSize: '1.3125rem' }}
                    onClick={handleExpandAllCatalog}
                    title={t('admin.expandAll')}
                    aria-label={t('admin.expandAll')}
                  >
                    ⤢
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    style={{ fontSize: '1.3125rem' }}
                    onClick={handleCollapseAllCatalog}
                    title={t('admin.collapseAll')}
                    aria-label={t('admin.collapseAll')}
                  >
                    ⤡
                  </Button>
                </div>
              </div>

              {productTreeByMainCategory.length === 0 ? (
                <Alert variant="info" className="mb-0">No categories or products found for tree view.</Alert>
              ) : (
                <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px', backgroundColor: '#fff' }}>
                  {productTreeByMainCategory.map((mainCategory) => (
                    <div key={mainCategory.id} style={{ marginBottom: '14px' }}>
                      <div
                        className="fw-bold d-flex align-items-center gap-2"
                        style={{
                          fontSize: '2rem',
                          border: dragOverMainCategoryId === String(mainCategory.id) ? '2px dashed #198754' : '1px solid transparent',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          backgroundColor: dragOverMainCategoryId === String(mainCategory.id) ? '#e8f6ee' : 'transparent'
                        }}
                        onDragOver={(event) => {
                          if (!draggedSubcategoryId && !draggedMainCategoryId) {
                            return;
                          }
                          event.preventDefault();
                          setDragOverMainCategoryId(String(mainCategory.id));
                        }}
                        onDragLeave={(event) => {
                          if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
                            setDragOverMainCategoryId('');
                          }
                        }}
                        onDrop={async (event) => {
                          event.preventDefault();

                          if (draggedMainCategoryId) {
                            const movedMainCategoryId = draggedMainCategoryId;
                            setDraggedMainCategoryId(null);
                            setDragOverMainCategoryId('');
                            await handleReorderMainCategory(movedMainCategoryId, mainCategory.id);
                            return;
                          }

                          const movedSubcategoryId = draggedSubcategoryId;
                          setDraggedSubcategoryId(null);
                          setDragOverMainCategoryId('');
                          if (!movedSubcategoryId) {
                            return;
                          }
                          await handleMoveSubcategoryToMainCategory(movedSubcategoryId, mainCategory.id);
                        }}
                      >
                        <span
                          draggable
                          title="Drag to reorder main categories"
                          onDragStart={(event) => {
                            setDraggedMainCategoryId(mainCategory.id);
                            event.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => {
                            setDraggedMainCategoryId(null);
                            setDragOverMainCategoryId('');
                          }}
                          style={{ cursor: 'grab', userSelect: 'none' }}
                        >
                          ⠿
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => toggleMainCategoryCollapsed(mainCategory.id)}
                          aria-label={isMainCategoryCollapsed(mainCategory.id) ? 'Expand main category' : 'Collapse main category'}
                        >
                          {isMainCategoryCollapsed(mainCategory.id) ? '▸' : '▾'}
                        </button>
                        <span>{mainCategory.name}</span>
                        <span className="text-muted small">({mainCategory.subcategories.length} subcategories)</span>
                        <span className="ms-auto d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ fontSize: '1.75rem', lineHeight: 1 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenCategoryModal({ category: mainCategory });
                            }}
                            title="Edit category"
                            aria-label="Edit category"
                          >
                            ✎
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ fontSize: '1.75rem', lineHeight: 1 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenCategoryModal({ categoryType: 'sub', parentId: mainCategory.id });
                            }}
                            title="Add subcategory"
                            aria-label="Add subcategory"
                          >
                            ⊞
                          </Button>
                        </span>
                      </div>

                      {!isMainCategoryCollapsed(mainCategory.id) && (
                      <div style={{ marginLeft: '20px', marginTop: '8px' }}>
                        {mainCategory.subcategories.map((subcategory) => {
                          const dropKey = `${mainCategory.id}:${subcategory.id}`;
                          const isDropTarget = dragOverSubcategoryKey === dropKey;
                          const isSubCollapsed = isSubcategoryCollapsed(subcategory.id);

                          return (
                            <div key={subcategory.id} style={{ marginBottom: '10px' }}>
                              <div
                                onDragOver={(event) => {
                                  if (!draggedProductId && !draggedSubcategoryId) {
                                    return;
                                  }
                                  event.preventDefault();
                                  if (dragOverSubcategoryKey !== dropKey && (draggedProductId || (draggedSubcategoryId && String(draggedSubcategoryId) !== String(subcategory.id)))) {
                                    setDragOverSubcategoryKey(dropKey);
                                  }
                                }}
                                onDragLeave={(event) => {
                                  if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
                                    setDragOverSubcategoryKey('');
                                  }
                                }}
                                onDrop={async (event) => {
                                  event.preventDefault();

                                  if (draggedSubcategoryId && String(draggedSubcategoryId) !== String(subcategory.id)) {
                                    const movedSubcategoryId = draggedSubcategoryId;
                                    setDraggedSubcategoryId(null);
                                    setDragOverSubcategoryKey('');
                                    setDragOverMainCategoryId('');
                                    await handleReorderSubcategory(movedSubcategoryId, subcategory.id);
                                    return;
                                  }

                                  const movedProductId = draggedProductId;
                                  setDraggedProductId(null);
                                  setDragOverSubcategoryKey('');
                                  if (!movedProductId) {
                                    return;
                                  }
                                  await handleMoveProductToSubcategory(movedProductId, subcategory.entity_id || subcategory.id);
                                }}
                                style={{
                                  padding: '6px 8px',
                                  borderRadius: '6px',
                                  border: isDropTarget ? '2px dashed #0d6efd' : '1px solid transparent',
                                  backgroundColor: isDropTarget ? '#e7f1ff' : 'transparent'
                                }}
                              >
                                <div
                                  className="fw-semibold d-flex align-items-center gap-2"
                                  draggable
                                  title="Drag subcategory to another main category"
                                  onDragStart={(event) => {
                                    setDraggedSubcategoryId(subcategory.id);
                                    event.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragEnd={() => {
                                    setDraggedSubcategoryId(null);
                                    setDragOverMainCategoryId('');
                                  }}
                                  style={{
                                    fontSize: '1.7857rem',
                                    border: '1px solid #d9dfe6',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    backgroundColor: '#f8f9fa',
                                    cursor: 'grab',
                                    userSelect: 'none'
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleSubcategoryCollapsed(subcategory.id);
                                    }}
                                    aria-label={isSubCollapsed ? 'Expand subcategory' : 'Collapse subcategory'}
                                  >
                                    {isSubCollapsed ? '▸' : '▾'}
                                  </button>
                                  <span>{subcategory.name}</span>
                                  <span className="text-muted">({subcategory.products.length})</span>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    draggable={false}
                                    style={{ fontSize: '1.75rem', lineHeight: 1 }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenCategoryModal({ category: subcategory });
                                    }}
                                    title="Edit category"
                                    aria-label="Edit category"
                                  >
                                    ✎
                                  </Button>
                                </div>

                                {!isSubCollapsed && (
                                <div style={{ marginLeft: '18px', marginTop: '6px' }}>
                                  {subcategory.products.length === 0 ? (
                                    <div className="small text-muted">Drop products here</div>
                                  ) : (
                                    subcategory.products.map((product) => {
                                      const isDraggingThis = draggedProductId === product.id;
                                      return (
                                        <div
                                          key={product.id}
                                          draggable
                                          onDragStart={() => setDraggedProductId(product.id)}
                                          onDragEnd={() => {
                                            setDraggedProductId(null);
                                            setDragOverSubcategoryKey('');
                                          }}
                                          style={{
                                            padding: '4px 8px',
                                            marginBottom: '4px',
                                            borderRadius: '4px',
                                            border: '1px solid #d9dfe6',
                                            backgroundColor: '#f8f9fa',
                                            cursor: isDraggingThis ? 'grabbing' : 'grab',
                                            opacity: isDraggingThis ? 0.5 : 1,
                                            fontSize: '1.6429rem'
                                          }}
                                        >
                                          • {product.name} <span className="text-muted small">(ID {product.id}, stock {product.stock})</span>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <Modal show={showCategoryModal} onHide={handleCloseCategoryModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCatalogCategory ? t('admin.editCategory') : t('admin.addCategory')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitCategoryForm}>
            <Form.Group className="mb-3">
              <Form.Label>Category type</Form.Label>
              <Form.Select
                name="category_type"
                value={categoryFormData.category_type}
                onChange={handleCategoryFormChange}
                disabled={Boolean(editingCatalogCategory)}
              >
                <option value="main">Main category</option>
                <option value="sub">Subcategory</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={categoryFormData.name}
                onChange={handleCategoryFormChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Slug *</Form.Label>
              <Form.Control
                type="text"
                name="slug"
                value={categoryFormData.slug}
                onChange={handleCategoryFormChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={categoryFormData.description}
                onChange={handleCategoryFormChange}
              />
            </Form.Group>

            {categoryFormData.category_type === 'sub' && (
              <Form.Group className="mb-3">
                <Form.Label>Manual URL</Form.Label>
                <Form.Control
                  type="url"
                  name="manual_url"
                  value={categoryFormData.manual_url}
                  onChange={handleCategoryFormChange}
                  placeholder="https://..."
                />
              </Form.Group>
            )}

            {categoryFormData.category_type === 'sub' && (
              <Form.Group className="mb-3">
                <Form.Label>Main category *</Form.Label>
                <Form.Select
                  name="parent_id"
                  value={categoryFormData.parent_id}
                  onChange={handleCategoryFormChange}
                  required
                >
                  <option value="">Select main category</option>
                  {categories.filter((category) => category.category_type === 'main').map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Display order</Form.Label>
              <Form.Control
                type="number"
                name="display_order"
                value={categoryFormData.display_order}
                onChange={handleCategoryFormChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_visible"
                label="Visible to customers"
                checked={categoryFormData.is_visible}
                onChange={handleCategoryFormChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCloseCategoryModal}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit">
                {t('common.save')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProduct ? t('admin.editProduct') : t('admin.addProduct')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Manual URL</Form.Label>
              <Form.Control
                type="url"
                name="manual_url"
                value={formData.manual_url}
                onChange={handleChange}
                placeholder="https://..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Manufacturer</Form.Label>
              <Form.Control
                type="text"
                name="manufacturer"
                value={formData.manufacturer}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Model</Form.Label>
              <Form.Control
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Product ID (unique for manufacturer)</Form.Label>
              <Form.Control
                type="text"
                name="product_id"
                value={formData.product_id}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Product Code (unique)</Form.Label>
              <Form.Control
                type="text"
                name="product_code"
                value={formData.product_code}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Subcategory *</Form.Label>
              <Form.Select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
              >
                <option value="">Select subcategory</option>
                {subcategoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.optionValue}>
                    {cat.parentName ? `${cat.parentName} -> ${cat.name}` : cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Stock</Form.Label>
              <Form.Control
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_visible"
                label="Visible to customers"
                checked={formData.is_visible}
                onChange={handleChange}
              />
            </Form.Group>

            {editingProduct && (
              <div className="mb-4">
                <h5 className="mb-2">Media Gallery</h5>
                <div className="media-preview-grid mb-3">
                  {(mediaOrder || []).map((media) => (
                    <div
                      key={media.id}
                      className="media-preview-item"
                      draggable
                      onDragStart={() => handleMediaDragStart(media.id)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => handleMediaDrop(media.id)}
                    >
                      {isVideo(media) ? (
                        <video className="media-preview" muted autoPlay loop playsInline preload="metadata">
                          <source src={getMediaUrl(media)} />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img className="media-preview" src={getMediaUrl(media)} alt="Product media" />
                      )}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        onClick={() => setCoverImage(media.id)}
                      >
                        Set as cover
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="mt-2"
                        onClick={() => handleDeleteMedia(editingProduct.id, media.id)}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  ))}
                  {(mediaOrder || []).length === 0 && (
                    <div className="text-muted">No media uploaded yet.</div>
                  )}
                </div>

                <Form.Group className="mb-2">
                  <Form.Label>Add Media (image or video)</Form.Label>
                  <Form.Control type="file" accept="image/*,video/*" multiple onChange={handleMediaSelect} />
                </Form.Group>

                <div
                  className={`media-dropzone ${isDragging ? 'dragging' : ''}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  Drag & drop images or videos here
                </div>

                {selectedMediaPreviews.length > 0 && (
                  <div className="media-preview-grid mt-2">
                    {selectedMediaPreviews.map((preview) => (
                      <div className="media-preview-item" key={preview.url}>
                        {preview.type.startsWith('video') ? (
                          <video className="media-preview" muted autoPlay loop playsInline preload="metadata">
                            <source src={preview.url} />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <img className="media-preview" src={preview.url} alt={preview.name} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  variant="outline-primary"
                  className="mt-2"
                  onClick={handleUploadSelectedMedia}
                  disabled={selectedMediaFiles.length === 0}
                >
                  Upload Selected Media
                </Button>
              </div>
            )}

            {editingProduct && (
              <div className="mb-4">
                <h5 className="mb-2">Linked Products</h5>
                {linkedLoading ? (
                  <div className="text-muted">Loading linked products...</div>
                ) : (
                  <div className="linked-products-list">
                    <div className="linked-products-selected">
                      {linkedProductIds.map((linkedId) => {
                        const linkedProduct = products.find((product) => product.id === linkedId);
                        if (!linkedProduct) {
                          return null;
                        }
                        return (
                          <div
                            key={linkedProduct.id}
                            className="linked-product-row"
                            draggable
                            onDragStart={() => handleLinkedDragStart(linkedProduct.id)}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => handleLinkedDrop(linkedProduct.id)}
                          >
                            <span className="linked-product-handle">⋮⋮</span>
                            <span>{linkedProduct.name}</span>
                          </div>
                        );
                      })}
                      {linkedProductIds.length === 0 && (
                        <div className="text-muted">No linked products selected.</div>
                      )}
                    </div>

                    <div className="linked-products-divider">Select products to link</div>

                    {products
                      .filter((product) => product.id !== editingProduct.id)
                      .map((product) => (
                        <Form.Check
                          key={product.id}
                          type="checkbox"
                          id={`linked-${product.id}`}
                          label={`${product.name} (ID ${product.id})`}
                          checked={linkedProductIds.includes(product.id)}
                          onChange={() => toggleLinkedProduct(product.id)}
                        />
                      ))}
                    {products.length <= 1 && (
                      <div className="text-muted">Add more products to link them.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit">
                {t('common.save')}
              </Button>
              <Button variant="secondary" onClick={handleCloseModal}>
                {t('common.cancel')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Import Products from Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Button 
              variant="link" 
              onClick={handleDownloadTemplate}
              className="p-0 mb-2"
            >
              📄 Download Excel Template
            </Button>
            <p className="text-muted small">
              Download the template to see the required format. Fill in your product data and upload below.
            </p>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Select Excel File *</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files[0])}
              disabled={importing}
            />
            <Form.Text className="text-muted">
              Only .xlsx and .xls files are supported
            </Form.Text>
          </Form.Group>

          {importResults && (
            <Alert variant={importResults.errors?.length > 0 ? 'warning' : 'success'}>
              <strong>Import Complete:</strong>
              <ul className="mb-0 mt-2">
                <li>✅ Created: {importResults.created}</li>
                <li>🔄 Updated: {importResults.updated}</li>
                <li>❌ Errors: {importResults.errors?.length || 0}</li>
              </ul>
              {importResults.errors?.length > 0 && (
                <div className="mt-2">
                  <strong>Errors:</strong>
                  <ul className="small">
                    {importResults.errors.map((item, idx) => (
                      <li key={idx}>{formatImportError(item)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Alert>
          )}

          <div className="d-grid gap-2">
            <Button 
              variant="primary" 
              onClick={handleImportProducts}
              disabled={!importFile || importing}
            >
              {importing ? 'Importing...' : 'Import Products'}
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportResults(null);
              }}
              disabled={importing}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminProducts;
